import type { ToolCall } from '#models/ToolCallModel.js';
import type { ToolBadgeSpec, ToolResultGroup } from './types.js';

import { LEGACY_WIDGET_NAMES, WidgetSchemas } from '@elara/domain/models/schemas/widget-schemas';

import { fetchDestinationDataSpec } from './specs/fetchDestinationData.js';
import { fetchCuratedHotelsSpec } from './specs/fetchCuratedHotels.js';
import { fetchHotelDataSpec } from './specs/fetchHotelData.js';
import { fetchPlaceDataSpec, fetchPlacesNearbySpec } from './specs/fetchPlaceData.js';
import { fetchSabreHotelDetailsSpec } from './specs/fetchSabreHotelDetails.js';
import { fetchFlightDataSpec, resumeFlightSearchWorkflowSpec } from './specs/flightSearch.js';
import {
  addToItinerarySpec,
  listItineraryItemsSpec,
  removeFromItinerarySpec,
  updateItineraryItemSpec,
} from './specs/itinerary.js';
import { fetchSabrePricesBatchedSpec, getWeatherSpec, legacySabreRoomRatesSpec, updateTripSpec } from './specs/misc.js';
import {
  fetchRoomAvailabilitySpec,
  fetchTravelportRoomsSpec,
  resumeRoomSearchWorkflowSpec,
} from './specs/roomSearch.js';
import {
  addShortlistGroupSpec,
  addToShortlistSpec,
  removeFromShortlistSpec,
  removeShortlistGroupSpec,
  reorderShortlistGroupsSpec,
  reorderShortlistItemsSpec,
  updateShortlistGroupSpec,
  updateShortlistItemSpec,
} from './specs/shortlist.js';
import { getUrlContentSpec, webScraperSpec, webSearchSpec } from './specs/webTools.js';

/** All tool-badge specs. Add new tools by importing their spec here. */
const ALL_SPECS: readonly ToolBadgeSpec[] = [
  // Data-fetching (expandable results)
  fetchCuratedHotelsSpec,
  fetchHotelDataSpec,
  fetchDestinationDataSpec,
  fetchPlaceDataSpec,
  fetchPlacesNearbySpec,
  resumeRoomSearchWorkflowSpec,
  fetchTravelportRoomsSpec,
  fetchFlightDataSpec,
  resumeFlightSearchWorkflowSpec,
  fetchSabreHotelDetailsSpec,
  webSearchSpec,
  webScraperSpec,
  getUrlContentSpec,
  // Plain-label tools
  fetchRoomAvailabilitySpec,
  getWeatherSpec,
  updateTripSpec,
  fetchSabrePricesBatchedSpec,
  legacySabreRoomRatesSpec,
  // Shortlist family
  addToShortlistSpec,
  removeFromShortlistSpec,
  updateShortlistItemSpec,
  addShortlistGroupSpec,
  removeShortlistGroupSpec,
  updateShortlistGroupSpec,
  reorderShortlistItemsSpec,
  reorderShortlistGroupsSpec,
  // Itinerary family
  addToItinerarySpec,
  removeFromItinerarySpec,
  updateItineraryItemSpec,
  listItineraryItemsSpec,
];

const SPEC_BY_NAME = new Map<string, ToolBadgeSpec>();
for (const spec of ALL_SPECS) {
  SPEC_BY_NAME.set(spec.toolName, spec);
  for (const alias of spec.aliases ?? []) {
    SPEC_BY_NAME.set(alias, spec);
  }
}

/** Tools that should never render a badge (widgets are rendered separately; 'pause' is a no-op). */
const HIDDEN_TOOL_NAMES = new Set<string>([
  ...Object.keys(WidgetSchemas),
  ...Object.keys(LEGACY_WIDGET_NAMES),
  'pause',
]);

/** Look up the spec for a tool name (or its legacy alias). */
export function findToolBadgeSpec(toolName: string): ToolBadgeSpec | undefined {
  return SPEC_BY_NAME.get(toolName);
}

/** True if the tool should be hidden from badge lists entirely (widgets, pause). */
export function isHiddenToolName(toolName: string): boolean {
  return HIDDEN_TOOL_NAMES.has(toolName);
}

/**
 * Returns the human-readable "in progress" label for a tool call, or undefined if the
 * tool is hidden or unknown. Used by ChatMessagesView to render the thinking text.
 */
export function getToolCallInProgressLabel(toolCall: ToolCall): string | undefined {
  if (HIDDEN_TOOL_NAMES.has(toolCall.toolName)) return undefined;
  const spec = findToolBadgeSpec(toolCall.toolName);
  if (!spec) return undefined;
  return spec.label(toolCall, true);
}

/** True if this tool call would render as a badge. Used by MessageUiState to track visible content. */
export function toolCallHasVisibleLabel(toolCall: ToolCall): boolean {
  if (HIDDEN_TOOL_NAMES.has(toolCall.toolName)) return false;
  return findToolBadgeSpec(toolCall.toolName) != null;
}

/**
 * Testing helper: look up a spec by name and run its `extractResults` against a
 * raw output blob (bypassing the ToolCall wrapper). Returns undefined for specs
 * that don't produce expandable results, or when no items are extracted.
 */
export function extractToolResults(
  toolName: string,
  output: unknown,
  input?: Record<string, unknown>,
): ToolResultGroup | undefined {
  const spec = findToolBadgeSpec(toolName);
  if (!spec?.extractResults) return undefined;
  const stubCall = { toolName, output, input } as unknown as ToolCall;
  const group = spec.extractResults(stubCall);
  if (!group || group.items.length === 0) return undefined;
  return group;
}

/** Build the collapsed "Found N X [context]" label for an expandable badge. */
export function buildResultsLabel(
  spec: ToolBadgeSpec,
  count: number,
  contextLabel?: string,
  resultNounOverride?: { singular: string; plural: string },
): string {
  const resultNoun = resultNounOverride ?? spec.resultNoun;
  const noun = resultNoun
    ? count === 1
      ? resultNoun.singular
      : resultNoun.plural
    : count === 1
      ? 'result'
      : 'results';
  const base = `Found ${count} ${noun}`;
  return contextLabel ? `${base} ${contextLabel}` : base;
}
